import styled from 'styled-components';

import { COLOR_TOKENS } from 'constants/quartz/color_tokens';

/**
 * Quartz-styled pagination footer used by the Banner Screen redesign
 * (GN-37779). All colors come from the Quartz 3.0 semantic tokens.
 *
 * Spacing follows the Figma footer: `12px 16px` padding, `space-12` (12px) gap
 * between elements in the left-hand group (rows-per-page selector + range text),
 * and the chevron nav group on the right.
 */
const ROBOTO = "'Roboto', Helvetica, Arial, sans-serif";

export const PaginationBarContainer = styled.div`
    align-items: center;
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    gap: 24px;
    width: 100%;
    padding: 12px 16px;
    border-top: 1px solid ${COLOR_TOKENS.border.default};
    font-family: ${ROBOTO};
`;

export const PaginationBarGroup = styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: 16px;
`;

export const PaginationBarNavGroup = styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: 8px;
`;

export const RangeText = styled.span`
    color: ${COLOR_TOKENS.text.secondary};
    font-family: ${ROBOTO};
    font-size: 14px;
    font-weight: 400;
    line-height: 150%;
    white-space: nowrap;
`;

export const NavButton = styled.button`
    align-items: center;
    background: transparent;
    border: none;
    cursor: pointer;
    display: inline-flex;
    justify-content: center;
    padding: 4px;
    color: ${COLOR_TOKENS.text.primary};

    &:focus {
        outline: none;
    }

    &:disabled {
        color: ${COLOR_TOKENS.text.disabled};
        cursor: not-allowed;
    }

    svg {
        display: block;
    }
`;

/*
 * ─────────────────────────────────────────────────────────────────────────
 * Banner Screen redesign (GN-37779) — three-zone pagination footer.
 *
 * All values are taken directly from the attached Figma specs:
 *   - Result summary / "Go to Page" label / page digits: Roboto 500, 14px,
 *     line-height 150%, Text/primary (#282828).
 *   - Chevron buttons: 24x24, radius-4, 1.25px Border/default (#97A4AD),
 *     Background/primary (#FFFFFF), padding space-4 (4px).
 *   - Page buttons: 24x24, radius-4, padding space-4. Selected page uses
 *     Background/active (#E0F5FF) with Text/selected (#3052DA); the other page
 *     numbers use Background/secondary (#F0F1F3) with Text/secondary (#5B6C79).
 *   - Ellipsis: 21x24, letter-spacing 32%.
 *   - Go-to-Page group: gap space-6 (6px), height 32px.
 *   - Input: 56x32, radius-4, 1px Border/default (#97A4AD).
 *   - Go button: height 32, radius-4, padding 6px 8px, Button/Primary (#9CDEFF).
 * ─────────────────────────────────────────────────────────────────────────
 */
export const FooterContainer = styled.div`
    align-items: center;
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
    padding: 12px 16px;
    border-top: 1px solid ${COLOR_TOKENS.border.default};
    font-family: ${ROBOTO};
`;

export const ResultSummary = styled.span`
    color: ${COLOR_TOKENS.text.primary};
    font-family: ${ROBOTO};
    font-size: 14px;
    font-weight: 500;
    line-height: 150%;
    white-space: nowrap;
`;

export const PageNavGroup = styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: 8px;
`;

export const ChevronButton = styled.button`
    align-items: center;
    background: ${COLOR_TOKENS.background.primary};
    border: 1.25px solid ${COLOR_TOKENS.border.default};
    border-radius: 4px;
    box-sizing: border-box;
    color: ${COLOR_TOKENS.text.primary};
    cursor: pointer;
    display: inline-flex;
    flex: 0 0 auto;
    height: 24px;
    justify-content: center;
    padding: 4px;
    width: 24px;

    &:focus {
        outline: none;
    }

    &:disabled {
        color: ${COLOR_TOKENS.text.disabled};
        border-color: ${COLOR_TOKENS.border.disabled};
        cursor: not-allowed;
    }

    svg {
        display: block;
    }
`;

export const PageButton = styled.button`
    align-items: center;
    background: ${({ isActive }) =>
        isActive ? COLOR_TOKENS.background.active : COLOR_TOKENS.background.secondary};
    border: none;
    border-radius: 4px;
    box-sizing: border-box;
    color: ${({ isActive }) =>
        isActive ? COLOR_TOKENS.text.selected : COLOR_TOKENS.text.secondary};
    cursor: pointer;
    display: inline-flex;
    flex: 0 0 auto;
    font-family: ${ROBOTO};
    font-size: 14px;
    font-weight: 500;
    height: 24px;
    justify-content: center;
    letter-spacing: 0.32em;
    line-height: 18px;
    padding: 4px;
    text-indent: 0.32em;
    width: 24px;

    &:focus {
        outline: none;
    }

    &:hover {
        background: ${({ isActive }) =>
            isActive ? COLOR_TOKENS.background.active : COLOR_TOKENS.background.secondary};
    }
`;

export const PageEllipsis = styled.span`
    align-items: center;
    color: ${COLOR_TOKENS.text.primary};
    display: inline-flex;
    flex: 0 0 auto;
    font-family: ${ROBOTO};
    font-size: 14px;
    font-weight: 500;
    height: 24px;
    justify-content: center;
    letter-spacing: 0.32em;
    line-height: 18px;
    text-indent: 0.32em;
    width: 21px;
`;

export const GoToPageGroup = styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: 6px;
`;

export const GoToPageLabel = styled.span`
    color: ${COLOR_TOKENS.text.primary};
    font-family: ${ROBOTO};
    font-size: 14px;
    font-weight: 500;
    line-height: 150%;
    white-space: nowrap;
`;

export const GoToPageInput = styled.input`
    background: ${COLOR_TOKENS.background.primary};
    border: 1px solid ${COLOR_TOKENS.border.default};
    border-radius: 4px;
    box-sizing: border-box;
    color: ${COLOR_TOKENS.text.primary};
    font-family: ${ROBOTO};
    font-size: 14px;
    height: 32px;
    padding: 0 8px;
    width: 56px;

    &:focus {
        outline: none;
        border-color: ${COLOR_TOKENS.form.input.borderFocus};
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    &[type='number'] {
        -moz-appearance: textfield;
    }
`;

export const GoButton = styled.button`
    align-items: center;
    background: ${COLOR_TOKENS.button.primary.background};
    border: none;
    border-radius: 4px;
    box-sizing: border-box;
    color: ${COLOR_TOKENS.button.primary.text};
    cursor: pointer;
    display: inline-flex;
    font-family: ${ROBOTO};
    font-size: 14px;
    font-weight: 500;
    height: 32px;
    justify-content: center;
    line-height: 150%;
    padding: 6px 8px;

    &:focus {
        outline: none;
    }

    &:hover:not(:disabled) {
        background: ${COLOR_TOKENS.button.primary.backgroundHover};
    }

    &:disabled {
        background: ${COLOR_TOKENS.button.primary.backgroundDisabled};
        color: ${COLOR_TOKENS.button.primary.textDisabled};
        cursor: not-allowed;
    }
`;
